"use client";

import { useEffect, useState } from "react";

import type {
  TableColumn,
  TablePaginationType,
  TableProps,
  TableRowType,
  TableSort,
} from "./Table.types";

export const useSort = <
  Row extends TableRowType,
  Group extends string = never,
>({
  defaultSort,
  controlledSort,
  onSortChange,
}: {
  defaultSort?: TableSort<Row>;
  controlledSort?: TableSort<Row>;
  onSortChange?: TableProps<Row, Group>["onSortChange"];
} = {}) => {
  const [localSort, setLocalSort] = useState<TableSort<Row>>(
    defaultSort ?? {
      key: undefined,
      order: "desc",
    },
  );

  const sort = { ...localSort, ...controlledSort };

  const handleSort = (column: TableColumn<Row, Group>) => {
    if (sort.key === undefined || sort.key !== column.id) {
      setLocalSort({
        key: column.id,
        order: "asc",
      });
      return;
    }

    if (sort.key === column.id) {
      if (sort.order === "asc") {
        setLocalSort({
          key: column.id,
          order: "desc",
        });
        return;
      }

      setLocalSort({
        key: undefined,
        order: "desc",
      });
    }
  };

  useEffect(() => {
    onSortChange?.(localSort);
  }, [localSort, onSortChange]);

  return {
    sort,
    setSort: setLocalSort,
    handleSort,
  };
};

export const usePagination = ({
  page = 1,
  pageSize = 10,
}: Partial<TablePaginationType> = {}) => {
  const [pagination, setPagination] = useState<TablePaginationType>({
    page,
    pageSize,
  });

  return {
    pagination,
    setPagination,
  };
};
